/* eslint-disable react-hooks/rules-of-hooks */
import { useCallback, useEffect, useState } from "react";
import { ApplicationUser } from "../auth/ApplicationUser";
import {
  attemptTokenRefresh,
  checkAndRefreshTokenIfNeeded,
  getSignInURL,
  refreshToken,
  signOut,
} from "../auth/authHelpers";
import { useGetCurrentUser } from "../user/api/getCurrentUser";
import { UserStatus } from "../user/types";
import { GandalfProps } from "../auth/authHelpers";

export type AuthResponse = {
  readonly user: ApplicationUser;
};

export const AUTH_SIGN_IN_FIRST_PARTY = "firstParty";
export const AUTH_SIGN_IN_THIRD_PARTY = "thirdParty";
export const IDENTITY_PROVIDER = "identity_provider";
export const AUTH_SIGN_IN_SOURCE = "authSignInSource";
//In session storage, store a variable that dictates first time launch for 3p so we can force a re-auth
export const FIRST_TIME_LAUNCH = "firstTimeLaunch";

export const useAuth = (): AuthResponse => {
  const [signedInUserEmail, setSignedInUserEmail] = useState<string | undefined>(undefined);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | undefined>(undefined);
  const [id, setId] = useState<string | undefined>(undefined);
  const [vibeId, setVibeId] = useState<string | undefined>(undefined);
  const [shouldFetchUser, setShouldFetchUser] = useState(false);
  const params = new URLSearchParams(sessionStorage.getItem("queryParams") || "");
  const identityProvider = params.get(IDENTITY_PROVIDER);
  const gandalfProps: GandalfProps | undefined = identityProvider
    ? {
        identity_provider: identityProvider,
        require_email_verification: "false",
      }
    : undefined;

  const setAuthSignInSource = (ip: string | null) => {
    const source: string = ip ? AUTH_SIGN_IN_THIRD_PARTY : AUTH_SIGN_IN_FIRST_PARTY;
    localStorage.setItem(AUTH_SIGN_IN_SOURCE, source);
  };

  const setFirstTimeLaunch = (value: string) => {
    sessionStorage.setItem(FIRST_TIME_LAUNCH, value);
  };

  const signOutAndRedirectToSignIn = () => {
    setIsLoggedIn(false);
    setShouldFetchUser(false);
    // Clear user data to prevent stale user info across multiple instances
    setSignedInUserEmail(undefined);
    setId(undefined);
    setVibeId(undefined);
    signOut().then(() => (window.location.href = getSignInURL(gandalfProps)));
  };

  /**
   * Callback function to check token expiration and handle token refresh
   *
   * @returns An async function that:
   * - Validates current token status
   * - Handles token refresh if needed
   * - Redirects to log in when necessary
   *
   * @memoized Using useCallback to prevent unnecessary re-renders
   */
  const checkTokenExpiration = useCallback(() => {
    return async (forceRefresh = false) => {
      try {
        // Always refresh token on page load
        if (forceRefresh) {
          const { success } = await attemptTokenRefresh();
          if (!success) {
            signOutAndRedirectToSignIn();
            return;
          }
        }
        const tokenStatus = await checkAndRefreshTokenIfNeeded();

        // Case 1: Token is invalid (expired or missing)
        if (!tokenStatus.isValid) {
          signOutAndRedirectToSignIn();
        }
        // Case 2: Token is valid but needed refresh, so we need to fetch user again
        else if (tokenStatus.isValid && (tokenStatus.refreshed || !isLoggedIn || !shouldFetchUser)) {
          setIsLoggedIn(true);
          setShouldFetchUser(true);
        }
      } catch (error) {
        signOutAndRedirectToSignIn();
      }
    };
  }, []);

  // First useEffect - handles auth flow
  useEffect(() => {
    // Check if it's a first time launch with 3p identity provider
    if (identityProvider && sessionStorage.getItem(FIRST_TIME_LAUNCH) !== "false") {
      // Set the flag to false so it doesn't redirect on subsequent launches.
      setFirstTimeLaunch("false");
      setAuthSignInSource(identityProvider);
      signOutAndRedirectToSignIn();
      return;
    }

    // Handle the case when a user signs in 1p/3p the first time or switches between 1p and 3p
    if (
      (identityProvider && localStorage.getItem(AUTH_SIGN_IN_SOURCE) !== AUTH_SIGN_IN_THIRD_PARTY) ||
      (!identityProvider && localStorage.getItem(AUTH_SIGN_IN_SOURCE) !== AUTH_SIGN_IN_FIRST_PARTY)
    ) {
      setAuthSignInSource(identityProvider);
      signOutAndRedirectToSignIn();
    } else {
      // Handle both 1p and 3p cases
      const checkTokens = checkTokenExpiration();
      // Force refresh token on page load
      checkTokens(true);
      // Set up periodic token checks every 2 minutes (120000 ms)
      const tokenCheckInterval = setInterval(() => checkTokens(false), 120000);

      return () => clearInterval(tokenCheckInterval);
    }
  }, []);

  // Only call useGetCurrentUser when shouldFetchUser is true
  const { data, error } = useGetCurrentUser({
    skip: !shouldFetchUser,
  });

  // Second useEffect - handles user data processing
  useEffect(() => {
    const handleAuth = async () => {
      if (error) {
        checkTokenExpiration();
      }

      if (data) {
        const currentUser = data.currentUser;
        if (currentUser.status !== UserStatus.ACTIVE) {
          signOutAndRedirectToSignIn();
        } else {
          setSignedInUserEmail(currentUser.emailAddress);
          setId(currentUser.id);
          setVibeId(currentUser.gandalfDetails.vibeId);
        }
      }
    };
    handleAuth();
  }, [data, error]);

  const potentialUser = new ApplicationUser({
    email: signedInUserEmail,
    isLoggedIn: isLoggedIn,
    id: id,
    vibeId: vibeId,
  });

  return {
    user: potentialUser,
  };
};
